/**
 * WorkflowInstanceProgress — Generic workflow instance progress view.
 *
 * Backend-driven: stepper states come from getWorkflow(workflowDefinitionId).states,
 * NOT hardcoded arrays. Works for any workflow type across all tenants/industries.
 *
 * Shows:
 *   - Workflow stepper (states from backend definition)
 *   - Task list with status badges (filtered by instance)
 *   - Document list with sign/view links
 *   - Overall completion progress
 *
 * DRR-055: Dumb Frontend — thin composition of backend data.
 */

import { useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Title,
  Text,
  Group,
  Stack,
  Paper,
  Card,
  Badge,
  ThemeIcon,
  Loader,
  Alert,
  Stepper,
  SimpleGrid,
  Progress,
  ActionIcon,
  Tooltip,
  Tabs,
} from '@mantine/core';
import {
  IconChevronLeft,
  IconCheck,
  IconClock,
  IconAlertTriangle,
  IconFileText,
  IconPencil,
  IconChevronRight,
  IconFlag,
} from '@tabler/icons-react';
import { useTenantId } from '@/auth';
import {
  useListTasksQuery,
  useListInstancesQuery,
  useListDocumentsQuery,
  useGetWorkflowQuery,
  GqlInstanceStatus,
  GqlStateType,
} from '@/generated/graphql';
import { normalizeMany } from '@/normalizers';
import { UnifiedStatus } from '@/types/normalized-entity';
import { SafetyBadge, StatusBadge } from '@/components/SafetyBadge';

/** Convert snake_case state name to Title Case label. */
function stateToLabel(state: string): string {
  return state
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function WorkflowInstanceProgress() {
  const { subjectId } = useParams<{ subjectId: string }>();
  const [searchParams] = useSearchParams();
  const instanceId = searchParams.get('instance');
  const tenantId = useTenantId() ?? '';
  const skip = !tenantId;
  const navigate = useNavigate();

  // Query instances, tasks, documents for tenant
  const { data: instancesData, loading: instancesLoading } = useListInstancesQuery({
    variables: { tenantId, limit: 50 },
    skip,
  });
  const { data: tasksData, loading: tasksLoading } = useListTasksQuery({
    variables: { limit: 200 },
    skip,
  });
  const { data: docsData, loading: docsLoading } = useListDocumentsQuery({
    variables: { tenantId, limit: 100 },
    skip,
  });

  // Find the target instance (from URL query param or first available)
  const instances = useMemo(
    () => instancesData?.listInstances?.items ?? [],
    [instancesData],
  );
  const targetInstance = useMemo(() => {
    if (instanceId) {
      return instances.find((i) => i.id === instanceId) ?? null;
    }
    return instances[0] ?? null;
  }, [instances, instanceId]);

  // Fetch workflow definition states from backend (zero hardcoded states)
  // workflowDefinitionId removed from InstanceFieldsFragment (DRR-124 internal field).
  // Workflow state list unavailable until a replacement linkage field is added.
  const { data: workflowData, loading: wfLoading } = useGetWorkflowQuery({
    variables: { workflowId: '' },
    skip: true,
  });

  const loading = instancesLoading || tasksLoading || docsLoading || wfLoading;

  // Backend-driven states: ordered from workflow definition
  const workflowStates = useMemo(() => {
    const states = workflowData?.getWorkflow?.states ?? [];
    // Preserve backend order (INITIAL first, FINAL last — already ordered in workflow definition)
    return states.map((s) => s.name);
  }, [workflowData]);

  const activeStepIndex = useMemo(() => {
    const currentState = targetInstance?.currentState ?? '';
    const idx = workflowStates.indexOf(currentState);
    return idx >= 0 ? idx : 0;
  }, [workflowStates, targetInstance]);

  // Normalize entities
  const normalizedTasks = useMemo(
    () => normalizeMany(tasksData?.taskApiListTasks?.items ?? []),
    [tasksData],
  );
  const normalizedDocs = useMemo(
    () => normalizeMany(docsData?.listDocuments?.nodes ?? []),
    [docsData],
  );

  // Filter tasks by the selected instance
  const activeInstanceId = targetInstance?.id;
  const instanceTasks = useMemo(
    () =>
      // workflowInstanceId removed from Task (DRR-124 internal field).
      // Show all tasks until a replacement instance-linkage field is available.
      activeInstanceId ? normalizedTasks : [],
    [normalizedTasks, activeInstanceId],
  );

  // Documents: show all tenant documents (backend will add instance filtering later)
  const instanceDocs = normalizedDocs;

  // Task stats
  const taskStats = useMemo(() => {
    const completed = instanceTasks.filter(
      (t) => t.unifiedStatus === UnifiedStatus.COMPLETED,
    ).length;
    const total = instanceTasks.length;
    return { completed, total, pct: total > 0 ? Math.round((completed / total) * 100) : 0 };
  }, [instanceTasks]);

  const workflowName = workflowData?.getWorkflow?.name;

  if (!tenantId) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconAlertTriangle size={16} />} color="yellow" title="No Tenant Selected">
          Please select a tenant to view the checklist.
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        {/* Header with back button */}
        <Group justify="space-between">
          <Group gap="md">
            <ActionIcon variant="subtle" onClick={() => navigate(-1)}>
              <IconChevronLeft size={20} />
            </ActionIcon>
            <div>
              <Title order={2}>{workflowName ?? 'Workflow Progress'}</Title>
              <Text c="dimmed" size="sm">
                {subjectId ? `Subject: ${subjectId}` : 'Workflow instance progress'}
                {targetInstance ? ` — ${targetInstance.instanceName ?? ''}` : ''}
              </Text>
            </div>
          </Group>
        </Group>

        {loading ? (
          <Group justify="center" py="xl">
            <Loader />
          </Group>
        ) : !targetInstance ? (
          <Alert icon={<IconAlertTriangle size={16} />} color="yellow" title="No Workflow Found">
            No workflow instance found.
          </Alert>
        ) : (
          <>
            {/* Workflow Stepper — states from backend definition */}
            <Card withBorder p="md">
              <Stack gap="sm">
                <Group justify="space-between">
                  <Text fw={500}>{workflowName ?? 'Progress'}</Text>
                  <Badge
                    color={targetInstance.status === GqlInstanceStatus.COMPLETED ? 'green' : 'blue'}
                  >
                    {targetInstance.status}
                  </Badge>
                </Group>
                {workflowStates.length > 0 ? (
                  <Stepper
                    active={activeStepIndex}
                    size="xs"
                    iconSize={20}
                  >
                    {workflowStates.map((state) => (
                      <Stepper.Step
                        key={state}
                        label={stateToLabel(state)}
                        completedIcon={<IconCheck size={12} />}
                      />
                    ))}
                  </Stepper>
                ) : (
                  <Text size="sm" c="dimmed">
                    Current state: {stateToLabel(targetInstance.currentState ?? '')}
                  </Text>
                )}
              </Stack>
            </Card>

            {/* Stats Cards */}
            <SimpleGrid cols={{ base: 2, sm: 3 }}>
              <Card withBorder padding="md">
                <Group justify="space-between" mb="xs">
                  <Text size="sm" c="dimmed">Tasks</Text>
                  <ThemeIcon variant="light" size="sm" color="blue">
                    <IconFlag size={14} />
                  </ThemeIcon>
                </Group>
                <Text size="xl" fw={700}>
                  {taskStats.completed}/{taskStats.total}
                </Text>
              </Card>
              <Card withBorder padding="md">
                <Group justify="space-between" mb="xs">
                  <Text size="sm" c="dimmed">Documents</Text>
                  <ThemeIcon variant="light" size="sm" color="teal">
                    <IconFileText size={14} />
                  </ThemeIcon>
                </Group>
                <Text size="xl" fw={700}>{instanceDocs.length}</Text>
              </Card>
              <Card withBorder padding="md">
                <Group justify="space-between" mb="xs">
                  <Text size="sm" c="dimmed">Completion</Text>
                  <ThemeIcon variant="light" size="sm" color="green">
                    <IconCheck size={14} />
                  </ThemeIcon>
                </Group>
                <Text size="xl" fw={700}>{taskStats.pct}%</Text>
              </Card>
            </SimpleGrid>

            {/* Task Completion Progress Bar */}
            {taskStats.total > 0 && (
              <Paper p="md" withBorder>
                <Group justify="space-between" mb="xs">
                  <Text size="sm" fw={500}>Task Progress</Text>
                  <Text size="sm" c="dimmed">
                    {taskStats.completed}/{taskStats.total}
                  </Text>
                </Group>
                <Progress
                  value={taskStats.pct}
                  color={taskStats.pct === 100 ? 'green' : 'blue'}
                  size="sm"
                  radius="xl"
                />
              </Paper>
            )}

            {/* Tasks + Documents Tabs */}
            <Tabs defaultValue="tasks">
              <Tabs.List>
                <Tabs.Tab value="tasks" leftSection={<IconFlag size={14} />}>
                  Tasks ({instanceTasks.length})
                </Tabs.Tab>
                <Tabs.Tab value="documents" leftSection={<IconFileText size={14} />}>
                  Documents ({instanceDocs.length})
                </Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="tasks" pt="md">
                <Stack gap="xs">
                  {instanceTasks.length === 0 ? (
                    <Paper p="xl" withBorder ta="center">
                      <ThemeIcon size="xl" variant="light" color="gray" mb="md">
                        <IconFlag size={24} />
                      </ThemeIcon>
                      <Text fw={500}>No tasks</Text>
                      <Text size="sm" c="dimmed">
                        No tasks found for this workflow instance.
                      </Text>
                    </Paper>
                  ) : (
                    instanceTasks.map((task) => (
                      <Paper key={task.id} p="sm" withBorder>
                        <Group justify="space-between" wrap="nowrap">
                          <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                            <ThemeIcon
                              color={
                                task.unifiedStatus === UnifiedStatus.COMPLETED
                                  ? 'green'
                                  : task.unifiedStatus === UnifiedStatus.ACTIVE
                                    ? 'blue'
                                    : task.unifiedStatus === UnifiedStatus.FAILED
                                      ? 'red'
                                      : 'gray'
                              }
                              variant="light"
                              size="sm"
                            >
                              {task.unifiedStatus === UnifiedStatus.COMPLETED ? (
                                <IconCheck size={12} />
                              ) : (
                                <IconClock size={12} />
                              )}
                            </ThemeIcon>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <Text size="sm" fw={500} lineClamp={1}>
                                {task.displayName}
                              </Text>
                              <Group gap="xs" mt={2}>
                                <StatusBadge status={task.unifiedStatus} size="xs" />
                                <Badge size="xs" variant="outline">
                                  {task.domainData.priority}
                                </Badge>
                                <SafetyBadge
                                  entity={task}
                                  size="xs"
                                  showDualApproval={false}
                                />
                              </Group>
                            </div>
                          </Group>
                        </Group>
                      </Paper>
                    ))
                  )}
                </Stack>
              </Tabs.Panel>

              <Tabs.Panel value="documents" pt="md">
                <Stack gap="xs">
                  {instanceDocs.length === 0 ? (
                    <Paper p="xl" withBorder ta="center">
                      <ThemeIcon size="xl" variant="light" color="gray" mb="md">
                        <IconFileText size={24} />
                      </ThemeIcon>
                      <Text fw={500}>No documents</Text>
                      <Text size="sm" c="dimmed">
                        No documents found for this workflow instance.
                      </Text>
                    </Paper>
                  ) : (
                    instanceDocs.map((doc) => (
                      <Paper key={doc.id} p="sm" withBorder>
                        <Group justify="space-between" wrap="nowrap">
                          <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                            <ThemeIcon variant="light" size="sm" color="teal">
                              <IconFileText size={12} />
                            </ThemeIcon>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <Text size="sm" fw={500} lineClamp={1}>
                                {doc.displayName}
                              </Text>
                              <Group gap="xs" mt={2}>
                                <StatusBadge status={doc.unifiedStatus} size="xs" />
                                <SafetyBadge
                                  entity={doc}
                                  size="xs"
                                  showDualApproval={false}
                                />
                              </Group>
                            </div>
                          </Group>
                          <Group gap={4}>
                            {doc.unifiedStatus === UnifiedStatus.PENDING && (
                              <Tooltip label="Sign Document">
                                <ActionIcon
                                  variant="light"
                                  color="blue"
                                  onClick={() =>
                                    navigate(`/ferp-core/documents/${doc.id}`)
                                  }
                                >
                                  <IconPencil size={14} />
                                </ActionIcon>
                              </Tooltip>
                            )}
                            <Tooltip label="View Document">
                              <ActionIcon
                                variant="subtle"
                                color="gray"
                                onClick={() =>
                                  navigate(`/ferp-core/documents/${doc.id}`)
                                }
                              >
                                <IconChevronRight size={14} />
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                        </Group>
                      </Paper>
                    ))
                  )}
                </Stack>
              </Tabs.Panel>
            </Tabs>
          </>
        )}
      </Stack>
    </Container>
  );
}

export default WorkflowInstanceProgress;
